import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Eye, EyeOff, ExternalLink, Save, ArrowLeft, Check, AlertTriangle, Bell, BellOff, BarChart3, Users, Eye as EyeIcon, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function getEmbedUrl(postUrl) {
  return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(postUrl)}&show_text=true&width=500`;
}

export default function Admin() {
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [currentUrl, setCurrentUrl] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertActive, setAlertActive] = useState(false);
  const [alertSaveStatus, setAlertSaveStatus] = useState("");
  const [alertSaveLoading, setAlertSaveLoading] = useState(false);

  const [analytics, setAnalytics] = useState(null);

  // Check existing token
  useEffect(() => {
    const saved = localStorage.getItem("admin_token");
    if (saved) {
      axios
        .get(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${saved}` } })
        .then(() => {
          setToken(saved);
          loadCurrentPost(saved);
          loadVoteAlert();
          loadAnalytics(saved);
        })
        .catch(() => localStorage.removeItem("admin_token"));
    }
  }, []);

  const loadCurrentPost = (t) => {
    axios.get(`${API}/api/facebook-post`).then((res) => {
      setCurrentUrl(res.data.url);
      setNewUrl(res.data.url);
    });
  };

  const loadVoteAlert = () => {
    axios.get(`${API}/api/vote-alert`).then((res) => {
      setAlertMessage(res.data.message || "");
      setAlertActive(res.data.active || false);
    });
  };

  const loadAnalytics = (t) => {
    axios
      .get(`${API}/api/admin/analytics`, { headers: { Authorization: `Bearer ${t}` } })
      .then((res) => setAnalytics(res.data))
      .catch(() => {});
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { password });
      setToken(data.token);
      localStorage.setItem("admin_token", data.token);
      loadCurrentPost(data.token);
      loadVoteAlert();
      loadAnalytics(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handlePreview = () => {
    try {
      const host = new URL(newUrl).hostname;
      if (host !== "facebook.com" && !host.endsWith(".facebook.com") && host !== "fb.watch") {
        setSaveStatus("error:Only facebook.com links are accepted.");
        return;
      }
    } catch {
      setSaveStatus("error:Please enter a valid URL.");
      return;
    }
    setPreviewUrl(newUrl);
    setSaveStatus("");
  };

  const isValidFacebookUrl = (url) => {
    try {
      const host = new URL(url).hostname;
      return host === "facebook.com" || host.endsWith(".facebook.com") || host === "fb.watch";
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    if (!isValidFacebookUrl(newUrl)) {
      setSaveStatus("error:Only facebook.com links are accepted.");
      return;
    }
    setSaveLoading(true);
    setSaveStatus("");
    try {
      await axios.put(
        `${API}/api/admin/facebook-post`,
        { url: newUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentUrl(newUrl);
      setSaveStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveStatus(`error:${typeof detail === "string" ? detail : "Save failed"}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("admin_token");
    setPassword("");
  };

  // Login screen
  if (!token) {
    return (
      <main className="pt-24 pb-12 bg-slate-950 min-h-screen" data-testid="admin-login-page">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-slate-100">Admin Access</h1>
            <p className="text-slate-400 mt-2">Enter your password to manage the site</p>
          </div>

          <form onSubmit={handleLogin} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {loginError && (
              <div className="flex items-center gap-2 text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg p-3" data-testid="login-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {loginError}
              </div>
            )}

            <Button
              type="submit"
              disabled={loginLoading || !password}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-5 rounded-full font-semibold"
              data-testid="admin-login-btn"
            >
              {loginLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-slate-400 hover:text-emerald-400 transition-colors">
              <ArrowLeft className="inline w-4 h-4 mr-1" />
              Back to homepage
            </Link>
          </div>
        </div>
      </main>
    );
  }

  // Admin dashboard
  return (
    <main className="pt-24 pb-12 bg-slate-950 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-100">Site Admin</h1>
            <p className="text-slate-400 mt-1">Manage homepage content and alerts</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="outline" className="bg-transparent border-slate-700 text-slate-300 hover:text-white rounded-full">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Home
              </Button>
            </Link>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-transparent border-slate-700 text-slate-300 hover:text-white rounded-full"
              data-testid="admin-logout-btn"
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {analytics && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 mb-6" data-testid="analytics-dashboard">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h2 className="font-serif text-xl font-semibold text-slate-100">Analytics</h2>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">
                  {Object.values(analytics.page_views.total).reduce((a, b) => a + b, 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total Page Views</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">
                  {Object.values(analytics.page_views.today).reduce((a, b) => a + b, 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Views Today</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-orange-400">
                  {analytics.rep_selections.total.email_sessions}
                </p>
                <p className="text-xs text-slate-400 mt-1">Email Sessions</p>
              </div>
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-orange-400">
                  {analytics.rep_selections.total.reps_selected}
                </p>
                <p className="text-xs text-slate-400 mt-1">Reps Contacted</p>
              </div>
            </div>

            {/* Page Views Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Page Views (All Time)</h3>
                <div className="space-y-2">
                  {["home", "about", "fact-sheet", "take-action", "resources"].map((page) => {
                    const total = analytics.page_views.total[page] || 0;
                    const maxVal = Math.max(...Object.values(analytics.page_views.total), 1);
                    const pct = Math.round((total / maxVal) * 100);
                    return (
                      <div key={page} className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-24 capitalize">{page.replace("-", " ")}</span>
                        <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-emerald-500 h-full rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-300 w-12 text-right">{total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Rep Selections</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Today</span>
                    <span className="text-slate-100">
                      {analytics.rep_selections.today.reps_selected} reps in {analytics.rep_selections.today.email_sessions} sessions
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">All time</span>
                    <span className="text-slate-100">
                      {analytics.rep_selections.total.reps_selected} reps in {analytics.rep_selections.total.email_sessions} sessions
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Facebook shares</span>
                    <span className="text-slate-100">{analytics.facebook_shares}</span>
                  </div>

                  {analytics.rep_selections.top_constituencies.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-slate-400 mb-2">Top Constituencies</h4>
                      {analytics.rep_selections.top_constituencies.slice(0, 5).map((c, i) => (
                        <div key={c.constituency} className="flex justify-between text-xs py-1">
                          <span className="text-slate-300">{i + 1}. {c.constituency}</span>
                          <span className="text-emerald-400">{c.count} reps</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Post */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 mb-6">
          <h2 className="font-serif text-xl font-semibold text-slate-100 mb-3">Current Facebook Post</h2>
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:text-emerald-300 text-sm break-all transition-colors"
          >
            {currentUrl}
            <ExternalLink className="inline ml-1 w-3 h-3" />
          </a>
        </div>

        {/* Update Post */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 mb-6">
          <h2 className="font-serif text-xl font-semibold text-slate-100 mb-4">Update Facebook Post</h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Paste a public Facebook post link</label>
              <Input
                type="url"
                placeholder="https://www.facebook.com/..."
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100"
                data-testid="facebook-url-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handlePreview}
                variant="outline"
                className="bg-transparent border-slate-700 text-slate-300 hover:text-white rounded-full"
                data-testid="preview-btn"
              >
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveLoading || !isValidFacebookUrl(newUrl)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full"
                data-testid="save-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                {saveLoading ? "Saving..." : "Save & Publish"}
              </Button>
            </div>

            {saveStatus === "success" && (
              <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-900/20 border border-emerald-800/30 rounded-lg p-3" data-testid="save-success">
                <Check className="w-4 h-4" />
                Facebook post updated. The homepage will now show this post.
              </div>
            )}
            {saveStatus.startsWith("error:") && (
              <div className="flex items-center gap-2 text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg p-3" data-testid="save-error">
                <AlertTriangle className="w-4 h-4" />
                {saveStatus.replace("error:", "")}
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {previewUrl && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6" data-testid="preview-section">
            <h2 className="font-serif text-xl font-semibold text-slate-100 mb-4">Preview</h2>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <iframe
                src={getEmbedUrl(previewUrl)}
                width="500"
                height="600"
                className="w-full max-w-[500px] mx-auto border-0 rounded-lg bg-white"
                style={{ maxWidth: "100%" }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                title="Facebook post preview"
                data-testid="preview-embed"
              />
            </div>
          </div>
        )}

        {/* Vote Alert Banner Management */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6" data-testid="vote-alert-admin">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-5 h-5 text-orange-400" />
            <h2 className="font-serif text-xl font-semibold text-slate-100">Vote Alert Banner</h2>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Set an alert message that appears as a banner at the top of every page. Visitors can dismiss it.
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Alert message</label>
              <Textarea
                placeholder="e.g. Committee Stage vote on the Defence (Amendment) Bill scheduled for 12 November — Contact your TD now!"
                value={alertMessage}
                onChange={(e) => setAlertMessage(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 min-h-[80px]"
                data-testid="alert-message-input"
              />
            </div>

            <div className="flex items-center gap-4">
              <Button
                onClick={async () => {
                  setAlertSaveLoading(true);
                  setAlertSaveStatus("");
                  try {
                    await axios.put(
                      `${API}/api/admin/vote-alert`,
                      { message: alertMessage, active: true },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setAlertActive(true);
                    setAlertSaveStatus("success:Alert is now live on the site.");
                  } catch {
                    setAlertSaveStatus("error:Failed to save alert.");
                  } finally {
                    setAlertSaveLoading(false);
                  }
                }}
                disabled={alertSaveLoading || !alertMessage.trim()}
                className="bg-orange-600 hover:bg-orange-500 text-white rounded-full"
                data-testid="activate-alert-btn"
              >
                <Bell className="w-4 h-4 mr-1" />
                {alertSaveLoading ? "Saving..." : "Activate Alert"}
              </Button>

              {alertActive && (
                <Button
                  onClick={async () => {
                    setAlertSaveLoading(true);
                    setAlertSaveStatus("");
                    try {
                      await axios.put(
                        `${API}/api/admin/vote-alert`,
                        { message: alertMessage, active: false },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setAlertActive(false);
                      setAlertSaveStatus("success:Alert has been turned off.");
                    } catch {
                      setAlertSaveStatus("error:Failed to deactivate alert.");
                    } finally {
                      setAlertSaveLoading(false);
                    }
                  }}
                  variant="outline"
                  className="bg-transparent border-slate-700 text-slate-300 hover:text-white rounded-full"
                  data-testid="deactivate-alert-btn"
                >
                  <BellOff className="w-4 h-4 mr-1" />
                  Turn Off
                </Button>
              )}
            </div>

            {alertActive && (
              <div className="flex items-center gap-2 text-sm text-orange-400 bg-orange-900/20 border border-orange-800/30 rounded-lg p-3">
                <Bell className="w-4 h-4" />
                Banner is currently <strong>live</strong> on the site.
              </div>
            )}

            {alertSaveStatus.startsWith("success:") && (
              <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-900/20 border border-emerald-800/30 rounded-lg p-3" data-testid="alert-save-success">
                <Check className="w-4 h-4" />
                {alertSaveStatus.replace("success:", "")}
              </div>
            )}
            {alertSaveStatus.startsWith("error:") && (
              <div className="flex items-center gap-2 text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg p-3" data-testid="alert-save-error">
                <AlertTriangle className="w-4 h-4" />
                {alertSaveStatus.replace("error:", "")}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
